import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { TextList, TextListItem } from '@naf-simba/elements-textlist';
import { Button } from '@naf-simba/elements-buttons';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import VehicleIcon from '@components/icons/VehicleIcon';
import NafCenterOffer from '@assets/images/NafCenterOffer.svg';
import { funnelPayload } from '@appTypes/gtmFunnel';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { mapSimbaVariant } from '@utils/simba';
import { useServiceCalculator } from '../context/ServiceCalculatorContext';
import * as S from '../Styles';

const TitleComponent = () => <Text variant={TextVariant.Header1}>Ditt serviceforslag</Text>;

const PriceOfferContentComponent = () => {
  const trackCTA = useTrackCTA();
  const manualRequestButtonVariant = mapSimbaVariant('secondary');
  const { offer, vehicleForm, updateShouldShowManualRequest, updateShouldShowAnOffer } = useServiceCalculator();
  const { licensePlateNumber, vehicleName, mileage } = vehicleForm.getValues();

  const selectedWorkItem = offer.vehicleMaintenanceWizard.mainWorkItems.find((x) => x.isSelected);

  return (
    <>
      <S.StyledVehicleText>
        <VehicleIcon />
        <Text variant={TextVariant.BodyTextHeader}>
          {licensePlateNumber} {vehicleName}, {mileage} km
        </Text>
      </S.StyledVehicleText>
      <S.StyledOfferCard>
        <Text variant={TextVariant.SubHeader} style={{ marginTop: 0 }}>
          {selectedWorkItem.text}
        </Text>
        <TextList variant="dotted">
          {selectedWorkItem.items.map((item) => (
            <TextListItem key={item.value}>{item.text}</TextListItem>
          ))}
        </TextList>
        <Text variant={TextVariant.BodyText} style={{ fontWeight: 700 }}>
          Din estimerte pris:
        </Text>
        <Text variant={TextVariant.Header2}>kr. {offer.vehicleMaintenanceWizard.offerRequest?.totalPrice},-</Text>
        <Button
          type="button"
          width="full"
          onClick={() => {
            trackCTA({
              cta_position: 'content',
              text: 'Finn ledig time',
              url: offer.vehicleMaintenanceWizard.nextAction.url,
            });
            window.open(offer.vehicleMaintenanceWizard.nextAction.url, '_blank');
          }}
        >
          Finn ledig time
        </Button>
      </S.StyledOfferCard>
      <Text variant={TextVariant.BodyText} style={{ marginTop: 16, marginBottom: 12 }}>
        Dette er prisen vi estimerer for din bil, basert på kjørte kilometer og alder. Prisen kan bli justert når vår
        mekaniker har sett på bilen din og gått igjennom servicehistorikken.
      </Text>
      <Text variant={TextVariant.SubHeader} style={{ marginBottom: 0 }}>
        Ønsker du tilbud på noe annet?
      </Text>
      <Text variant={TextVariant.BodyText}>
        Send oss en forespørsel med beskrivelse av hva du ønsker, så kommer vi tilbake til deg med et tilbud per e-post.
      </Text>
      <S.StyledBottomButton
        type="button"
        variant={manualRequestButtonVariant.variant}
        color={manualRequestButtonVariant.color}
        shade={manualRequestButtonVariant.shade}
        onClick={() => {
          trackCTA({ cta_position: 'content', text: 'Be om pris' });
          updateShouldShowManualRequest(true);
          updateShouldShowAnOffer(false);
        }}
      >
        Be om pris
      </S.StyledBottomButton>
    </>
  );
};

export const PriceOffer = () => {
  const trackCTA = useTrackCTA();
  const resetButtonVariant = mapSimbaVariant('outline');
  const { resetState } = useServiceCalculator();

  useSendGTMEventOnce(funnelPayload('Servicekalkulator', 3), []);

  return (
    <S.StyledWrapper>
      <BreadCrumb paddingTop={false} />
      <S.DesktopWrapper>
        <S.StyledInnerContent>
          <TitleComponent />
          <PriceOfferContentComponent />
        </S.StyledInnerContent>
        <S.StyledConfirmationImageWrapper>
          <NafCenterOffer />
        </S.StyledConfirmationImageWrapper>
      </S.DesktopWrapper>
      <S.MobileWrapper>
        <S.StyledInnerContent>
          <S.StyledMobileHeader>
            <TitleComponent />
            <S.StyledConfirmationImageWrapper>
              <NafCenterOffer />
            </S.StyledConfirmationImageWrapper>
          </S.StyledMobileHeader>
          <PriceOfferContentComponent />
        </S.StyledInnerContent>
      </S.MobileWrapper>
      <S.StyledBottomButton
        type="button"
        variant={resetButtonVariant.variant}
        color={resetButtonVariant.color}
        shade={resetButtonVariant.shade}
        onClick={() => {
          trackCTA({ cta_position: 'content', text: 'Kalkulér på nytt' });
          resetState();
        }}
      >
        Kalkulér på nytt
      </S.StyledBottomButton>
    </S.StyledWrapper>
  );
};
